// @ts-nocheck
/**
 * Breaks list — redesigned to obd3-gui pattern (FillPage + PageHeader +
 * DataTable + Switch status toggle + DropdownMenu row actions + ConfirmDialog
 * + shadcn Dialog for Create). Preserves duration validation (integer 1-1440)
 * and ACL read/add/edit/delete gates. Inline Create modal (v1 quirk kept).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Coffee,
  Download,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Timer,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_BREAK_LIST,
  API_BREAK_CREATE,
  API_BREAK_UPDATE,
  API_BREAK_DELETE,
} from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
import { USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const BreakList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [searchValue, setSearchValue] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newBreak, setNewBreak] = useState<any>({
    title: '',
    duration: '',
    description: '',
    status: true,
  });
  const [breaksData, setBreaksData] = useState<any[]>([]);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [loggedInUserRole, setLoggedInUserRole] = useState('');
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(
        parseModuleOperations(user.acl_settings.module_operations),
      );
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    getApiCall(API_BREAK_LIST, {}).then(onListSuccess).catch(onError).finally(() =>
      setLoading(false),
    );
    const user = getUser();
    if (user) {
      setLoggedInUserRole(user?.role || '');
      if (user?.acl_settings) {
        setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  function onListSuccess(resp: any) {
    setBreaksData(resp || []);
  }

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
      toast.error(friendlyApiError(err) || 'Session expired');
    } else {
      console.log(err);
      toast.error(friendlyApiError(err) || 'Failed to load breaks');
    }
  }

  const refetch = () =>
    getApiCall(API_BREAK_LIST, {}).then(onListSuccess).catch(onError);

  const handleStatusChange = (id: any, status: any) => {
    const newStatus = status == '1' ? '0' : '1';
    const url = API_BREAK_UPDATE.replace(':id', id);
    postApiCall(url, { status: newStatus })
      .then(() => {
        toast.success('Break updated successfully');
        refetch();
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err) || 'Failed to update break');
        }
      });
  };

  // DataTable's built-in globalFilter handles name search.
  const filteredBreaks = breaksData;

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    if (name === 'duration') {
      // Digits only (keyboard/paste already block -, +, ., e, but IME/mobile
      // keyboards can slip past those guards), capped at 4 chars and clamped
      // to the 1-1440 range so an out-of-range value can't even be typed.
      let sanitized = value.replace(/[^0-9]/g, '').slice(0, 4);
      if (sanitized !== '') {
        const num = Number(sanitized);
        if (num > 1440) sanitized = '1440';
        else if (num === 0) sanitized = '1';
      }
      setNewBreak((prev: any) => ({ ...prev, duration: sanitized }));
      return;
    }

    setNewBreak((prev: any) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setNewBreak({
      title: '',
      duration: '',
      description: '',
      type: 'Scheduled',
      status: true,
    });
  };

  const handleCreateBreak = () => {
    if (!newBreak.title.trim()) {
      toast.error('Break Title is required');
      return;
    }
    if (!newBreak.duration || newBreak.duration.toString().trim() === '') {
      toast.error('Break Duration is required');
      return;
    }
    const durationNum = Number(newBreak.duration);
    if (isNaN(durationNum)) {
      toast.error('Break Duration must be a valid number');
      return;
    }
    if (durationNum <= 0) {
      toast.error('Break Duration must be greater than 0');
      return;
    }
    if (durationNum > 1440) {
      toast.error('Break Duration must not exceed 1440 minutes (24 hours)');
      return;
    }
    if (!Number.isInteger(durationNum)) {
      toast.error('Break Duration must be a whole number (no decimals)');
      return;
    }

    const breakWithId = {
      title: newBreak.title,
      duration: durationNum.toString(),
      description: newBreak.description,
      type: 'Scheduled',
      status: newBreak.status === true ? '1' : '0',
    };

    setSaving(true);
    postApiCall(API_BREAK_CREATE, breakWithId)
      .then(() => {
        toast.success('Break created successfully');
        setShowCreateModal(false);
        resetForm();
        refetch();
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err) || 'Failed to create break');
        }
      })
      .finally(() => setSaving(false));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    postApiCall(API_BREAK_DELETE, { id: pendingDelete.id })
      .then(() => {
        toast.success('Break deleted successfully');
        setPendingDelete(null);
        refetch();
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err) || 'Failed to delete break');
        }
      })
      .finally(() => setDeleting(false));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'breaks-list',
        data: filteredBreaks,
        columns: [
          { header: 'Name', accessor: (row: any) => row.title || '-' },
          {
            header: 'Duration',
            accessor: (row: any) => `${row.duration || 0} Minutes`,
          },
          { header: 'Description', accessor: (row: any) => row.description || '-' },
          {
            header: 'Status',
            accessor: (row: any) => (row.status == '1' ? 'Active' : 'Inactive'),
          },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const isBreaksRole =
    loggedInUserRole === USER_ROLES.SUPERADMIN ||
    loggedInUserRole === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead =
    isBreaksRole && hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.BREAKS, 'read');
  const canAdd =
    isBreaksRole && hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.BREAKS, 'add');
  const canEdit =
    isBreaksRole && hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.BREAKS, 'edit');
  const canDelete =
    isBreaksRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.BREAKS, 'delete');
  const showActions = canEdit || canDelete;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        accessorKey: 'title',
        header: 'Name',
        size: 240,
        cell: ({ row }) => {
          const nm = row.original.title || '—';
          const initials = (nm || '')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w: string) => w[0])
            .join('')
            .toUpperCase() || '—';
          return (
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
                {initials}
              </span>
              {canEdit ? (
                <Link
                  to={`/breaks/${row.original.id}`}
                  className="font-medium text-foreground hover:text-primary hover:underline"
                >
                  {nm}
                </Link>
              ) : (
                <span className="font-medium text-foreground">{nm}</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'duration',
        header: 'Duration',
        size: 130,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
            <Timer className="h-3 w-3" />
            <span className="tabular-nums">{row.original.duration} min</span>
          </span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) =>
          row.original.description ? (
            <span className="line-clamp-2 text-xs text-muted-foreground">
              {row.original.description}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ row }) => (
          <Switch
            checked={row.original.status == '1'}
            onCheckedChange={() =>
              handleStatusChange(row.original.id, row.original.status)
            }
            disabled={!canEdit}
            aria-label="Toggle status"
          />
        ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 110,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                title={`Edit ${row.original.title}`}
                onClick={() => navigate(`/breaks/${row.original.id}`)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                title={`Delete ${row.original.title}`}
                onClick={() => setPendingDelete(row.original)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [canEdit, canDelete, showActions, navigate]);

  // Stat strip data
  const totalBreaks = breaksData.length;
  const activeBreaks = breaksData.filter((b: any) => b.status == '1').length;
  const totalDuration = breaksData.reduce(
    (sum: number, b: any) => sum + (Number(b.duration) || 0),
    0,
  );

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Breaks" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading && breaksData.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Breaks"
        description="Named break types agents can select from — with duration guardrails per break."
        actions={
          <>
            {breaksData.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4" />
                New Break
              </Button>
            )}
          </>
        }
      />

      {/* Stat strip — Total / Active / Total-duration */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs shadow-neu-raised">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Coffee className="h-3.5 w-3.5" />
          </span>
          <span className="text-muted-foreground">Total breaks</span>
          <span className="font-semibold text-foreground tabular-nums">{totalBreaks}</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-emerald-900/60 dark:bg-emerald-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5" />
          </span>
          <span className="text-emerald-700 dark:text-emerald-300">Active</span>
          <span className="font-semibold text-emerald-800 tabular-nums dark:text-emerald-200">
            {activeBreaks}
          </span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-sky-900/60 dark:bg-sky-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300">
            <Timer className="h-3.5 w-3.5" />
          </span>
          <span className="text-sky-700 dark:text-sky-300">Combined duration</span>
          <span className="font-semibold text-sky-800 tabular-nums dark:text-sky-200">
            {totalDuration} min
          </span>
        </div>
      </div>

      <DataTable
        tableId="breaks:list:v1"
        columns={columns}
        data={filteredBreaks}
        isLoading={loading}
        searchPlaceholder="Search breaks by name…"
        emptyTitle="No breaks yet"
        emptyDescription="Create a break to let agents pause with a labeled reason."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        pageSize={10}
        disableExport
      />

      {/* Create modal */}
      <Dialog
        open={showCreateModal}
        onOpenChange={(o) => {
          if (!o) {
            setShowCreateModal(false);
            resetForm();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coffee className="h-4 w-4 text-primary" />
              Add Break
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">
                Break title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                type="text"
                name="title"
                value={newBreak.title}
                onChange={handleInputChange}
                placeholder="e.g. Tea break"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">
                Duration (minutes) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="duration"
                type="number"
                name="duration"
                value={newBreak.duration}
                onChange={handleInputChange}
                placeholder="Enter duration in minutes"
                min="1"
                max="1440"
                step="1"
                onKeyDown={(e: any) => {
                  if (
                    e.key === '-' ||
                    e.key === 'e' ||
                    e.key === '+' ||
                    e.key === '.'
                  ) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e: any) => {
                  const pastedValue = e.clipboardData.getData('text');
                  const numValue = Number(pastedValue);
                  if (
                    isNaN(numValue) ||
                    numValue <= 0 ||
                    numValue > 1440 ||
                    !Number.isInteger(numValue)
                  ) {
                    e.preventDefault();
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                Whole minutes between 1 and 1440 (24 hours).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                rows={3}
                name="description"
                value={newBreak.description}
                onChange={handleInputChange}
                placeholder="Optional — what this break is for."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
            >
              Discard
            </Button>
            <Button onClick={handleCreateBreak} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Creating…' : 'Create break'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this break?"
        description={
          <>
            Break{' '}
            <span className="font-medium text-foreground">{pendingDelete?.title}</span>{' '}
            will be permanently deleted. Agents will no longer be able to select it.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default BreakList;
